import { fetchAuth } from "."
import { i18n } from "../i18n"

/** @type {import("vuex").Module} */
export const configurationStore = {
  namespaced: true,

  state: {
    configurationAmounts: [],
    configuration: null,
    configurationBom: null,

    emptyModulePlacements: [],
    requiredFuseBoxes: [],
    feedingValidation: null,
    forbiddenToDrop: null
  },

  getters: {
    configurationUrl(state, _getters, rootState) {
      if (rootState.projects.project == null || state.configuration == null) return
      return `${process.env.VUE_APP_API}/projects/${rootState.projects.project.number}/configurations/${state.configuration.index}`
    }
  },

  mutations: {
    changeConfigurationAmounts: (state, amounts) => state.configurationAmounts = amounts,
    setConfiguration: (state, { configuration }) => {
      state.configuration = configuration
    },
    setActiveConfiguration: (state, configurationId) => {
      if (configurationId == null) return
      state.configuration = state.configurationAmounts[configurationId].configuration
      state.configuration.index = +configurationId
    },
    clearActiveConfiguration: (state) => state.configuration = null,
    setConfigurationBom: (state, bom) => state.configurationBom = bom,

    setEmptyModulePlacements: (state, placements) => state.emptyModulePlacements = placements,
    setRequiredFuseBoxes: (state, fuseBoxes) => state.requiredFuseBoxes = fuseBoxes,
    setFeedingValidation: (state, feedingValidation) => state.feedingValidation = feedingValidation,
    setForbiddenToDrop: (state, forbiddenToDrop) => state.forbiddenToDrop = forbiddenToDrop,

    addModulePlacement: (state, placement) => {
      state.configuration.modulePlacements.push({
        ...placement,
        id: Math.random(),
      })
    },
    deleteModulePlacement: (state, placement) => {
      const index = state.configuration.modulePlacements
        .findIndex((p) => p.position.side == placement.side && p.position.gridIndex == placement.gridIndex)
      if (index != -1) state.configuration.modulePlacements.splice(index, 1)
    },
  },

  actions: {
    async loadConfigurations({ rootGetters, commit }) {
      const response = await fetchAuth(
        `${process.env.VUE_APP_API}/projects/${rootGetters.projectNumber}/configurations`
      )
      commit('changeConfigurationAmounts', await response.json())
    },
    async saveConfiguration({ rootGetters, state, commit }, index = rootGetters.configurationIndex) {
      const configuration = index == rootGetters.configurationIndex ? state.configuration : state.configurationAmounts[index].configuration
      commit("changeConfigurationAmounts", state.configurationAmounts.map((ca, i) => {
        return i == index ? { ...ca, configuration } : ca
      }))
      await fetchAuth(
        `${process.env.VUE_APP_API}/projects/${rootGetters.projectNumber}/configurations/${index}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: state.configurationAmounts[index].amount, configuration }),
        }
      )
    },
    async createNewConfiguration({ state, rootGetters }) {
      const configuration = createNewConfiguration(state.configurationAmounts)
      const response = await fetchAuth(
        `${process.env.VUE_APP_API}/projects/${rootGetters.projectNumber}/configurations`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(configuration),
        }
      )
      return response.json()
    },
    async deleteConfiguration({ rootGetters }, configurationIndex) {
      await fetchAuth(
        `${process.env.VUE_APP_API}/projects/${rootGetters.projectNumber}/configurations/${configurationIndex}`,
        { method: "DELETE" }
      )
    },
    async generateBom({ getters, commit }) {
      const res = await fetchAuth(`${getters.configurationUrl}/bom`)
      commit("setConfigurationBom", await res.json())
    },

    async loadEmptyModulePlacements({ commit, rootGetters }) {
      let response = await fetchAuth(
        `${rootGetters["configurations/configurationUrl"]}/empty-module-placements`
      )
      commit('setEmptyModulePlacements', await response.json())
    },
    async loadInvalidPlacements({ dispatch, commit, rootGetters }, { itemNumber, ignore }) {
      const response = await fetchAuth(
        `${rootGetters["configurations/configurationUrl"]}/invalid-placements/${itemNumber}` +
          (ignore == null || ignore < 0 ? `` : `?ignore=${ignore}`)
      )
      dispatch('configurations/generateBom', null, { root: true })
      return commit('setForbiddenToDrop', await response.json())
    },
    async loadRequiredFuseBoxes({ commit, rootGetters }) {
      const response = await fetchAuth(
        `${rootGetters["configurations/configurationUrl"]}/required-fuse-boxes`
      )
      commit('setRequiredFuseBoxes', await response.json())
    },
    async validateFeedIn({ commit, rootGetters }) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      let response = await fetchAuth(
        `${rootGetters["configurations/configurationUrl"]}/feed-in-validation`
      )
      commit('setFeedingValidation', await response.json())
    },
  },
}

function createNewConfiguration(configurationAmounts) {
  const nameRegex = /^(.+) \((\d+)\)$/
  const names = configurationAmounts
    ? configurationAmounts.map((conf) => conf.configuration.configurationName)
    : []

  let newName = i18n.t("configuration.newConfiguration")
  while (names.includes(newName)) {
    let match = newName.match(nameRegex)
    if (match) newName = `${match[1]} (${+match[2] + 1})`
    else newName = `${newName} (2)`
  }

  return {
    configurationName: newName,
    columnType: 'XS2',
    height: 1000,
    mountingOrientation: 'BOTTOM_UP',
    mountingMaterial: 'CONCRETE',
    jibCrane: null,
    modulePlacements: [],
    prewired: false,
    electricProtection: null,
    feedInLocation: 'MODULE',
  }
}
